import React, { useEffect, useState } from 'react';
import { getDataFreshness, type DataFreshness as Freshness } from '../api/dashboardApi';

/** SRS #15 — "Data as of" indicator that reflects real refresh times.
 *  Shows the newest successful source refresh; hover for the per-source
 *  breakdown and the next expected run. Never fakes "now". */

export function relativeTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return 'not yet refreshed';
  const diff = now - new Date(iso).getTime();
  const abs = Math.abs(diff);
  const unit = abs < 3600e3 ? [Math.round(abs / 60e3), 'min'] as const
    : abs < 86400e3 ? [Math.round(abs / 3600e3), 'h'] as const
    : [Math.round(abs / 86400e3), 'd'] as const;
  if (unit[0] === 0) return diff >= 0 ? 'just now' : 'any moment';
  return diff >= 0 ? `${unit[0]} ${unit[1]} ago` : `in ${unit[0]} ${unit[1]}`;
}

const DOT: Record<string, string> = { fresh: 'var(--ds-success)', stale: 'var(--ds-warning)', pending: 'var(--ds-text-faint)', paused: 'var(--ds-text-faint)' };

export default function DataFreshness({ compact = false }: { compact?: boolean }) {
  const [data, setData] = useState<Freshness | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getDataFreshness().then(d => { if (!cancelled) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!data) return null;
  const stale = data.sources.some(s => s.status === 'stale');
  const label = data.last_refreshed_at
    ? `Data refreshed ${relativeTime(data.last_refreshed_at)}`
    : 'Data refresh pending';
  return (
    <span style={{ position: 'relative', display: 'inline-block' }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        title={data.last_refreshed_at ? new Date(data.last_refreshed_at).toLocaleString() : undefined}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: compact ? 11 : 12, color: 'var(--ds-text-muted)', background: 'transparent', border: '1px solid #e4e7ec', borderRadius: 999, padding: compact ? '2px 8px' : '4px 10px', cursor: 'pointer' }}
      >
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: stale ? DOT.stale : DOT.fresh }} />
        {label}
        {data.next_expected_at && !compact && <span style={{ color: 'var(--ds-text-faint)' }}>· next {relativeTime(data.next_expected_at)}</span>}
      </button>
      {open && (
        <div role="dialog" aria-label="Data refresh by source" style={{ position: 'absolute', right: 0, top: '110%', zIndex: 20, minWidth: 300, background: 'var(--ds-surface)', border: '1px solid #e4e7ec', borderRadius: 10, boxShadow: '0 8px 24px rgba(16,24,40,.12)', padding: 12, fontSize: 12, color: 'var(--ds-text-secondary)' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Refresh by source</div>
          {data.sources.map(s => (
            <div key={s.source_system} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: DOT[s.status] }} />
              <span style={{ flex: 1 }}>{s.label}</span>
              <span style={{ color: 'var(--ds-text-muted)' }}>
                {s.status === 'paused' ? 'paused' : s.status === 'pending' ? 'pending' : relativeTime(s.last_refreshed_at)}
              </span>
            </div>
          ))}
          <div style={{ marginTop: 8, color: 'var(--ds-text-faint)' }}>
            Sources refresh on their own schedule (daily for US sources and news, weekly for others).
            {data.next_expected_at && <> Next expected {relativeTime(data.next_expected_at)}.</>}
          </div>
        </div>
      )}
    </span>
  );
}
